import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { nomeNoBucket } from '../feed/feedLogica';
import {
  normalizarConversa,
  normalizarMensagem,
  PAGINA,
  type Anexo,
  type Conversa,
  type Mensagem,
  type Pessoa,
} from './mensagensLogica';

/**
 * Acesso às Mensagens (migrations 250/251). Quem vê o quê e quem fala com
 * quem é decidido no banco (RLS + RPC); aqui só se pede.
 */

const BUCKET = 'mensagens';
const CAMPOS = 'id, conversa_id, autor_id, autor_nome, texto, anexos, created_at, apagada_em';

// Tabelas e RPC das 250/251 ainda fora de database.generated.ts.
const sb = supabase as unknown as {
  from: (table: string) => any;
  rpc: (fn: string, args?: Record<string, unknown>) => any;
  channel: (topico: string, opcoes?: Record<string, unknown>) => any;
  removeChannel: (canal: unknown) => unknown;
  realtime?: { setAuth?: (token?: string | null) => unknown };
};

/** Mudou algo aqui mesmo (enviei, li): menu e lista recarregam. */
export const EVENTO_MENSAGENS = 'north:mensagens-mudou';
export const avisarMensagensMudou = () => window.dispatchEvent(new Event(EVENTO_MENSAGENS));

/** A pessoa está olhando a tela (aba visível, ou janela com foco). */
export const naTela = () => document.visibilityState === 'visible' || document.hasFocus();

const falha = (error: { message?: string } | null, contexto: string) => {
  if (error) throw new Error(`${contexto}: ${error.message ?? 'erro desconhecido'}`);
};

const rpc = async <T>(fn: string, args: Record<string, unknown> | undefined, contexto: string): Promise<T> => {
  const { data, error } = await sb.rpc(fn, args);
  falha(error, contexto);
  return data as T;
};

const linhas = (data: unknown) => (Array.isArray(data) ? (data as Record<string, unknown>[]) : []);

// ── Conversas ───────────────────────────────────────────────────────────────
/** Cria, se faltar, o meu chat com a operação e o(s) grupo(s) da minha equipe. */
export const preparar = () => rpc<void>('msg_preparar', undefined, 'Não foi possível abrir as mensagens');

export const listarConversas = async (): Promise<Conversa[]> =>
  linhas(await rpc('msg_minhas_conversas', undefined, 'Não foi possível carregar as conversas')).map(
    normalizarConversa,
  );

export const abrirDireta = (outro: string) =>
  rpc<string>('msg_abrir_direta', { p_outro: outro }, 'Não foi possível abrir a conversa');

export const abrirOperacao = (de: string) =>
  rpc<string>('msg_abrir_operacao', { p_de: de }, 'Não foi possível abrir a conversa');

export const listarContatos = async (): Promise<Pessoa[]> =>
  linhas(await rpc('msg_contatos', undefined, 'Não foi possível carregar os contatos')).map((r) => ({
    userId: String(r.user_id),
    nome: String(r.nome ?? ''),
    papel: String(r.papel ?? ''),
  }));

export const listarPessoasDaOperacao = async (): Promise<Pessoa[]> =>
  linhas(await rpc('msg_pessoas_operacao', undefined, 'Não foi possível carregar as pessoas')).map((r) => ({
    userId: String(r.user_id),
    nome: String(r.nome ?? ''),
    papel: '',
  }));

export const souDaOperacao = async (): Promise<boolean> => {
  const { data } = await sb.rpc('msg_sou_operacao');
  return data === true;
};

export const participantes = async (conversa: string): Promise<Pessoa[]> =>
  linhas(await rpc('msg_participantes', { p_conversa: conversa }, 'Não foi possível carregar os participantes')).map(
    (r) => ({ userId: String(r.user_id), nome: String(r.nome ?? ''), papel: String(r.papel ?? '') }),
  );

// ── Mensagens ───────────────────────────────────────────────────────────────
/** As últimas `PAGINA` mensagens (ou as anteriores a `antes`), da mais antiga para a mais nova. */
export async function listarMensagens(conversa: string, antes?: string): Promise<Mensagem[]> {
  let q = sb.from('msg_mensagens').select(CAMPOS).eq('conversa_id', conversa);
  if (antes) q = q.lt('created_at', antes);
  const { data, error } = await q.order('created_at', { ascending: false }).limit(PAGINA);
  falha(error, 'Não foi possível carregar as mensagens');
  return linhas(data).map(normalizarMensagem).reverse();
}

/** As que chegaram depois de `depois` — a conferida periódica, para quando o tempo real falhar. */
export async function mensagensDepois(conversa: string, depois: string): Promise<Mensagem[]> {
  const { data, error } = await sb
    .from('msg_mensagens')
    .select(CAMPOS)
    .eq('conversa_id', conversa)
    .gt('created_at', depois)
    .order('created_at', { ascending: true })
    .limit(200);
  falha(error, 'Não foi possível atualizar as mensagens');
  return linhas(data).map(normalizarMensagem);
}

export async function buscarMensagem(id: string): Promise<Mensagem | null> {
  const { data } = await sb.from('msg_mensagens').select(CAMPOS).eq('id', id).maybeSingle();
  return data ? normalizarMensagem(data as Record<string, unknown>) : null;
}

/**
 * Envia: primeiro os arquivos (pasta da conversa), depois a mensagem com a
 * lista deles. Autor, nome e hora são carimbados pelo banco.
 */
export async function enviar(conversa: string, texto: string, arquivos: File[]): Promise<Mensagem | null> {
  const anexos: Anexo[] = [];
  for (const f of arquivos) {
    const caminho = `${conversa}/${crypto.randomUUID()}-${nomeNoBucket(f.name)}`;
    const { error } = await supabase.storage.from(BUCKET).upload(caminho, f, { contentType: f.type || undefined });
    falha(error, `Não foi possível enviar ${f.name}`);
    anexos.push({ caminho, nome: f.name, tipo: f.type || 'application/octet-stream', tamanho: f.size });
  }
  const { data, error } = await sb
    .from('msg_mensagens')
    .insert({ conversa_id: conversa, texto: texto.trim(), anexos })
    .select(CAMPOS)
    .maybeSingle();
  falha(error, 'Não foi possível enviar a mensagem');
  avisarMensagensMudou();
  return data ? normalizarMensagem(data as Record<string, unknown>) : null;
}

export const apagar = async (id: string) => {
  await rpc<void>('msg_apagar', { p_mensagem: id }, 'Não foi possível apagar a mensagem');
  avisarMensagensMudou();
};

export const marcarLida = async (conversa: string) => {
  const { error } = await sb.rpc('msg_marcar_lida', { p_conversa: conversa });
  if (!error) avisarMensagensMudou();
};

/** URLs temporárias (1 h) dos arquivos — o bucket é privado. */
export async function urlsDosArquivos(caminhos: string[]): Promise<Map<string, string>> {
  if (!caminhos.length) return new Map();
  try {
    const { data } = await supabase.storage.from(BUCKET).createSignedUrls(caminhos, 3600);
    return new Map(
      ((data ?? []) as { path: string | null; signedUrl: string }[])
        .filter((d) => d.path && d.signedUrl)
        .map((d) => [d.path!, d.signedUrl]),
    );
  } catch {
    return new Map();
  }
}

// ── Super admin ─────────────────────────────────────────────────────────────
export const salvarGrupo = (conversa: string | null, titulo: string, membros: string[]) =>
  rpc<string>(
    'msg_salvar_grupo',
    { p_conversa: conversa, p_titulo: titulo.trim(), p_membros: membros },
    'Não foi possível salvar o grupo',
  );

export const listarOperacao = async (): Promise<(Pessoa & { naOperacao: boolean })[]> =>
  linhas(await rpc('msg_operacao_lista', undefined, 'Não foi possível carregar a operação')).map((r) => ({
    userId: String(r.user_id),
    nome: String(r.nome ?? ''),
    papel: '',
    naOperacao: r.na_operacao === true,
  }));

export const definirOperacao = (pessoa: string, naOperacao: boolean) =>
  rpc<void>(
    'msg_operacao_definir',
    { p_user: pessoa, p_na_operacao: naOperacao },
    'Não foi possível alterar a operação',
  );

export const auditoriaConversas = async (pessoa: string): Promise<Conversa[]> =>
  linhas(await rpc('msg_auditoria_conversas', { p_user: pessoa }, 'Não foi possível carregar as conversas')).map(
    normalizarConversa,
  );

/** Lê pela Auditoria: a primeira página fica registrada (mensagens / conversa_lida). */
export const auditoriaMensagens = async (conversa: string, antes?: string): Promise<Mensagem[]> =>
  linhas(
    await rpc(
      'msg_auditoria_mensagens',
      { p_conversa: conversa, p_antes: antes ?? null, p_limite: 200 },
      'Não foi possível carregar as mensagens',
    ),
  )
    .map((r) => normalizarMensagem({ ...r, conversa_id: conversa }))
    .reverse();

// ── Tempo real ──────────────────────────────────────────────────────────────
/**
 * Um canal privado por pessoa ("msg:<uuid>", migration 250), compartilhado
 * por quem estiver ouvindo (menu e tela) — abrir dois canais iguais no mesmo
 * cliente dá conflito no supabase-js.
 */
export type AvisoTempoReal = { evento: 'mensagem' | 'apagada'; conversaId: string; mensagemId: string };
type Ouvinte = (aviso: AvisoTempoReal) => void;

const ouvintes = new Set<Ouvinte>();
let canal: unknown = null;
let canalDe: string | null = null;

const repassar = (evento: AvisoTempoReal['evento']) => (msg: { payload?: Record<string, unknown> }) => {
  const p = msg?.payload ?? {};
  if (!p.conversa_id) return;
  const aviso = { evento, conversaId: String(p.conversa_id), mensagemId: String(p.mensagem_id ?? '') };
  ouvintes.forEach((o) => o(aviso));
};

// Sem tempo real (Realtime fora, canal recusado), a tela segue com a
// conferida periódica — por isso nenhum erro daqui sobe.
const abrirCanal = async (eu: string) => {
  if (canalDe === eu) return;
  if (canal) sb.removeChannel(canal);
  canal = null;
  canalDe = eu;
  try {
    // Canal privado exige o token da sessão no Realtime.
    await sb.realtime?.setAuth?.();
  } catch {
    /* segue sem */
  }
  if (canalDe !== eu || !ouvintes.size) return;
  try {
    canal = sb
      .channel(`msg:${eu}`, { config: { private: true } })
      .on('broadcast', { event: 'mensagem' }, repassar('mensagem'))
      .on('broadcast', { event: 'apagada' }, repassar('apagada'))
      .subscribe();
  } catch {
    canal = null;
  }
};

/** Passa a receber os avisos; devolve a função que para de ouvir. */
export function ouvirMensagens(eu: string, ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte);
  void abrirCanal(eu);
  return () => {
    ouvintes.delete(ouvinte);
    if (!ouvintes.size) {
      if (canal) sb.removeChannel(canal);
      canal = null;
      canalDe = null;
    }
  };
}
